import React, { useMemo, useState } from 'react';
import { observer, useLocalStore } from 'mobx-react';
import { useHistory } from 'react-router';
import { capitalize, toLower } from 'lodash';

import { Button, Display } from 'Views/Components/Button/Button';
import { TextField } from 'Views/Components/TextBox/TextBox';
import FilterContainer from 'Views/Components/FilterContainer/FilterContainer';
import StudentList from 'Views/Components/StudentList/StudentList';

import {
	getStudyPlanConditions, getUserEntityFilters, IUserEntityFilterState,
} from 'Views/Pages/Frontend/UserManagement/FilterHelpers';
import useDebounce from 'Hooks/useDebounce';
import { DatePicker } from 'Views/Components/DatePicker/DatePicker';
import { DisplayType } from 'Views/Components/Models/Enums';
import { UserStatus } from 'Util/UserUtils';
import { startCase } from 'lodash';
import { action } from 'mobx';
import { hasAnyRole } from 'Util/SecurityUtils';
import ProgramSearch from 'Views/Components/Program/ProgramSearch';
import { Select } from 'Views/Components/Select/Select';
import useStore from 'Hooks/useStore';

const StudentListWithFilters = observer(() => {
	const history = useHistory();
	const store = useStore();
	const [searchValue, setSearchValue] = useState<string>();
	const filterModel = useLocalStore<IUserEntityFilterState>(() => ({}));

	const [showFilters, setFilterVisibility] = useState(false);
	const searchFieldStore = useLocalStore(() => ({ value: undefined }));

	const resetFilters = action(() => {
		filterModel.programCode = undefined;
		filterModel.programTitle = undefined;
		filterModel.status = undefined;
		filterModel.lastAccessedAfter = undefined;
		filterModel.lastAccessedBefore = undefined;
	});

	const setSearchDebounced = useDebounce(
		(abortSignals, newSearchValue) => setSearchValue(newSearchValue),
		[],
	);

	const statusFilterOptions = Object.keys(UserStatus)
		.map(status => ({
			display: startCase(status.toLowerCase()),
			value: status,
		}));

	const [studentFilters, studyPlanFilters] = useMemo(
		() => {
			const userFilters = getUserEntityFilters(filterModel);
			const initialStudyPlanFilters = getStudyPlanConditions(filterModel);

			if (filterModel.programCode || filterModel.programTitle) {
				initialStudyPlanFilters.push([
					{
						path: 'active',
						comparison: 'equal',
						value: ['true'],
					},
				]);
			}

			return [userFilters, initialStudyPlanFilters];
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps, max-len
		[filterModel.status, filterModel.programCode, filterModel.programTitle, filterModel.lastAccessedAfter, filterModel.lastAccessedBefore],
	);

	return (
		<div className="filtered-staff-list">
			<div className="list-row">
				<div className="search-bar">
					<TextField
						className="search-input"
						placeholder="Search students"
						model={searchFieldStore}
						modelProperty="value"
						onAfterChange={evt => setSearchDebounced(evt.target.value)}
					/>
				</div>
				<div className="search-actions">
					<Button
						display={showFilters ? Display.Outline : Display.Solid}
						icon={{ icon: 'filter-list', iconPos: 'icon-right' }}
						onClick={() => setFilterVisibility(prev => !prev)}
						testId="students-list-collection-search-filter-btn"
					>
						Filter
					</Button>
					{
						hasAnyRole(store, ['Admin', 'InstitutionAdmin', 'StudentAdvisor'])
							? (
								<Button
									display={Display.Solid}
									icon={{ icon: 'add', iconPos: 'icon-right' }}
									onClick={() => history.push('users/student/create')}
								>
									Add student
								</Button>
							) : null
					}
				</div>
			</div>
			<div className="list-row">
				<div className="filters">
					<FilterContainer
						showFilters={showFilters}
						onClear={resetFilters}
					>
						<ProgramSearch<IUserEntityFilterState>
							labelVisible={false}
							label="Program code"
							placeholder={`${capitalize(window.lexicon.program)} ${toLower(window.lexicon.code)}`}
							model={filterModel}
							modelProperty="programCode"
							displayType={DisplayType.INLINE}
							searchByProperty="code"
						/>
						<ProgramSearch<IUserEntityFilterState>
							labelVisible={false}
							label="Program title"
							placeholder={`${capitalize(window.lexicon.program)} ${toLower(window.lexicon.title)}`}
							model={filterModel}
							modelProperty="programTitle"
							displayType={DisplayType.INLINE}
							searchByProperty="displayName"
						/>
						<Select
							labelVisible={false}
							label="Status"
							placeholder="Status"
							model={filterModel}
							modelProperty="status"
							options={statusFilterOptions}
							displayType={DisplayType.INLINE}
							inputProps={{
								'aria-label': 'status',
							}}
						/>
						<DatePicker
							model={filterModel}
							modelProperty="lastAccessedAfter"
							name="AccessedAfter"
							label="Accessed after"
							placeholder="Accessed after"
							labelVisible={false}
							displayType={DisplayType.INLINE}
						/>
						<DatePicker
							model={filterModel}
							modelProperty="lastAccessedBefore"
							name="AccessedBefore"
							label="Accessed before"
							placeholder="Accessed before"
							labelVisible={false}
							displayType={DisplayType.INLINE}
						/>
					</FilterContainer>
				</div>
			</div>
			<div className="list-row">
				<StudentList
					searchTerm={searchValue}
					studentFilters={studentFilters}
					studyPlanFilters={studyPlanFilters}
				/>
			</div>
		</div>
	);
});

export default StudentListWithFilters;
